import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Search, Download, Filter, CheckCircle2, XCircle, Clock, Loader2, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTIONS = [
  { v: "all", l: "All", color: "bg-slate-600" },
  { v: "pending", l: "Pending", color: "bg-amber-500" },
  { v: "in_progress", l: "In Progress", color: "bg-blue-500" },
  { v: "resolved", l: "Resolved", color: "bg-emerald-500" },
  { v: "rejected", l: "Rejected", color: "bg-red-500" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function LiveSupportSubmissions() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [filterForm, setFilterForm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterForm) params.set("form_key", filterForm);
    if (filterStatus) params.set("status", filterStatus);
    if (search.trim()) params.set("q", search.trim());
    if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
    if (dateTo) {
      const d = new Date(dateTo);
      d.setHours(23, 59, 59, 999);
      params.set("date_to", d.toISOString());
    }
    try {
      const { data } = await api.get(`/admin/live-support/submissions?${params.toString()}`);
      setItems(data?.items || []);
      setCounts(data?.counts || {});
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [filterForm, filterStatus, search, dateFrom, dateTo]);

  useEffect(() => {
    // initial form list
    api.get("/admin/live-support/forms").then(({ data }) => setForms(data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filterForm) params.set("form_key", filterForm);
      if (filterStatus) params.set("status", filterStatus);
      if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
      if (dateTo) {
        const d = new Date(dateTo); d.setHours(23, 59, 59, 999);
        params.set("date_to", d.toISOString());
      }
      const res = await api.get(`/admin/live-support/submissions-export.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `live_support_submissions_${Date.now()}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="ls-submissions-page">
      <PageHeader title="Live Support Submissions" subtitle="All form reports submitted via the live chat widget (deposit pending, withdraw, bank deposit etc.)" />

      {/* Status chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_OPTIONS.map((s) => {
          const c = s.v === "all" ? total : (counts[s.v] || 0);
          return (
            <button key={s.v} onClick={() => setFilterStatus(s.v)} data-testid={`ls-sub-filter-${s.v}`}
                    className={`h-8 px-3 rounded-full text-xs font-bold inline-flex items-center gap-2 border ${filterStatus === s.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
              <span className={`h-2 w-2 rounded-full ${s.color}`} /> {s.l}
              <span className="px-1.5 h-4 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold inline-flex items-center">{c}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-sm p-3 mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="username / player id / phone / transaction id ..."
                   data-testid="ls-sub-search"
                   className="w-full h-9 pl-8 pr-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Form</label>
          <select value={filterForm} onChange={(e) => setFilterForm(e.target.value)} data-testid="ls-sub-filter-form"
                  className="h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900 min-w-[170px]">
            <option value="">All forms</option>
            {forms.map((f) => <option key={f.id} value={f.key}>{f.title}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 className="h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 className="h-9 px-2 border border-gray-200 rounded-sm text-sm bg-white text-gray-900" />
        </div>
        <button onClick={() => { setSearch(""); setFilterForm(""); setFilterStatus("all"); setDateFrom(""); setDateTo(""); }}
                className="h-9 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 inline-flex items-center gap-1">
          <Filter size={12} /> Clear
        </button>
        <button onClick={exportCsv} data-testid="ls-sub-export"
                className="h-9 px-3 rounded-sm bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold inline-flex items-center gap-1">
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-600 font-bold">
                <th className="px-3 py-2.5">Time</th>
                <th className="px-3 py-2.5">Ticket #</th>
                <th className="px-3 py-2.5">Form</th>
                <th className="px-3 py-2.5">User</th>
                <th className="px-3 py-2.5">Player ID</th>
                <th className="px-3 py-2.5">Key Fields</th>
                <th className="px-3 py-2.5">Attach</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Handled By</th>
                <th className="px-3 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="p-8 text-center text-gray-500"><Loader2 size={14} className="animate-spin inline mr-2" />Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={10} className="p-10 text-center text-gray-500">No submissions match these filters.</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-emerald-50/30">
                  <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtTime(r.created_at)}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded font-mono text-[11px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200">
                      #{r.ticket_no || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-bold text-gray-900 text-[13px]">{r.form_title}</div>
                    <div className="text-[10px] font-mono text-gray-500">{r.form_key}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-gray-900">@{r.username || "—"}</div>
                    {r.user_phone && <div className="text-[11px] text-gray-500">📞 {r.user_phone}</div>}
                  </td>
                  <td className="px-3 py-2.5 font-mono font-bold text-purple-700">{r.player_id || "—"}</td>
                  <td className="px-3 py-2.5">
                    <KeyFieldsSummary fields={r.fields || []} />
                  </td>
                  <td className="px-3 py-2.5">
                    {(r.attachments || []).length > 0 ? (
                      <div className="flex gap-1">
                        {r.attachments.slice(0, 2).map((a, i) => {
                          const url = a.url?.startsWith("http") ? a.url : `${BACKEND_URL}${a.url}`;
                          const isImage = (a.mime || "").startsWith("image/");
                          return (
                            <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                              {isImage ? (
                                <img src={url} alt="" className="h-10 w-10 object-cover rounded border border-gray-200" />
                              ) : (
                                <div className="h-10 w-10 rounded border border-gray-200 flex items-center justify-center text-sm bg-gray-50">📄</div>
                              )}
                            </a>
                          );
                        })}
                        {r.attachments.length > 2 && <span className="text-[10px] text-gray-500 self-end">+{r.attachments.length - 2}</span>}
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-2.5">
                    {r.resolved_by ? (
                      <div>
                        <div className="font-semibold text-gray-900 text-[12px] inline-flex items-center gap-1">
                          <span className="h-4 w-4 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center text-[9px] font-black">{(r.resolved_by[0] || "?").toUpperCase()}</span>
                          {r.resolved_by}
                        </div>
                        {r.resolved_at && <div className="text-[10px] text-gray-500">{fmtTime(r.resolved_at)}</div>}
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-400 italic">unassigned</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => setViewing(r)} data-testid={`ls-sub-view-${r.id}`}
                            className="h-8 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold inline-flex items-center gap-1">
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewing && (
        <SubmissionDrawer
          submission={viewing}
          onClose={() => setViewing(null)}
          onUpdated={() => { setViewing(null); load(); }}
        />
      )}
    </div>
  );
}

function KeyFieldsSummary({ fields }) {
  // Show first 2 textual fields and one number
  const text = fields.filter((f) => !f.url).slice(0, 3);
  if (text.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="space-y-0.5">
      {text.map((f, i) => (
        <div key={i} className="text-[11px] leading-snug">
          <span className="text-gray-500">{f.label}:</span> <span className="font-mono font-semibold text-gray-900 break-all">{f.value}</span>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { c: "bg-amber-50 text-amber-700 border-amber-200", l: "Pending", I: Clock },
    in_progress: { c: "bg-blue-50 text-blue-700 border-blue-200", l: "In Progress", I: Loader2 },
    resolved: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", l: "Resolved", I: CheckCircle2 },
    rejected: { c: "bg-red-50 text-red-700 border-red-200", l: "Rejected", I: XCircle },
  };
  const s = map[status] || map.pending;
  const Icon = s.I;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${s.c}`}>
      <Icon size={10} /> {s.l}
    </span>
  );
}

function SubmissionDrawer({ submission, onClose, onUpdated }) {
  const [detail, setDetail] = useState(submission);
  const [note, setNote] = useState(submission.resolution_note || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // fetch full thread for context
    api.get(`/admin/live-support/submissions/${submission.id}`)
       .then(({ data }) => setDetail(data?.submission || submission))
       .catch(() => {});
  }, [submission.id, submission]);

  const update = async (newStatus) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/live-support/submissions/${submission.id}`, {
        status: newStatus,
        resolution_note: note || null,
      });
      toast.success(`Marked ${newStatus}`);
      if (data?.submission) setDetail({ ...detail, ...data.submission });
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" data-testid="ls-sub-drawer">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded font-mono text-[11px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200">
                #{detail.ticket_no || "—"}
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Ticket</span>
            </div>
            <div className="font-extrabold text-gray-900 truncate">📋 {detail.form_title}</div>
            <div className="text-[11px] text-gray-500">{fmtTime(detail.created_at)} · {detail.form_key}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={detail.status} />
            <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-lg">✕</button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Handled-by agent card (only after resolution) */}
          {detail.resolved_by && (
            <div className="bg-gradient-to-r from-emerald-50 to-transparent border border-emerald-200 rounded-sm p-3.5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500 text-white font-black flex items-center justify-center text-[14px]">
                {(detail.resolved_by[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                  {detail.status === "resolved" ? "✅ Resolved by" : detail.status === "rejected" ? "❌ Rejected by" : "Handled by"}
                </div>
                <div className="font-extrabold text-gray-900 text-[14px]">{detail.resolved_by}</div>
                {detail.resolved_at && <div className="text-[11px] text-gray-600">{fmtTime(detail.resolved_at)}</div>}
              </div>
              <a href="#" onClick={(e) => {
                e.preventDefault();
                const tk = localStorage.getItem("admin_token") || localStorage.getItem("token");
                fetch(`${BACKEND_URL}/api/admin/live-support/submissions/${detail.id}/pdf`, { headers: tk ? { Authorization: `Bearer ${tk}` } : {} })
                  .then((r) => r.ok ? r.blob() : null)
                  .then((b) => {
                    if (!b) return;
                    const u = URL.createObjectURL(b);
                    const a = document.createElement("a");
                    a.href = u; a.download = `maxlive88-ticket-${detail.ticket_no || detail.id}.pdf`; a.click();
                    setTimeout(() => URL.revokeObjectURL(u), 1000);
                  });
              }}
              data-testid="ls-sub-download-pdf"
              className="px-3 h-9 rounded-sm bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-xs font-bold inline-flex items-center gap-1.5">
                <Download size={12} /> PDF
              </a>
            </div>
          )}

          {/* User card */}
          <div className="bg-gradient-to-r from-purple-50 to-transparent border border-purple-100 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-purple-500 text-white font-bold text-lg flex items-center justify-center">
                {(detail.username || "U").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-gray-900">@{detail.username || "—"}</div>
                <div className="text-[12px] text-gray-600">
                  Player ID: <span className="font-mono font-bold text-purple-700">{detail.player_id || "—"}</span>
                  {detail.full_name && <span> · {detail.full_name}</span>}
                </div>
                {detail.user_phone && <div className="text-[12px] text-gray-600">📞 {detail.user_phone}</div>}
              </div>
            </div>
          </div>

          {/* Field rows */}
          <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
            {(detail.fields || []).map((f, i) => (
              <div key={i} className="px-4 py-2.5 grid grid-cols-[140px_1fr] gap-3">
                <div className="text-[12px] text-gray-500 font-bold">{f.label}</div>
                <div className="text-sm font-mono text-gray-900 break-all">
                  {f.url ? (
                    <a href={f.url.startsWith("http") ? f.url : `${BACKEND_URL}${f.url}`} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                      {f.value} <ExternalLink size={12} />
                    </a>
                  ) : (f.value || "—")}
                </div>
              </div>
            ))}
          </div>

          {/* Attachments grid */}
          {(detail.attachments || []).length > 0 && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Attachments</div>
              <div className="grid grid-cols-3 gap-2">
                {detail.attachments.map((a, i) => {
                  const url = a.url?.startsWith("http") ? a.url : `${BACKEND_URL}${a.url}`;
                  const isImage = (a.mime || "").startsWith("image/");
                  return (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="block rounded border border-gray-200 hover:opacity-90 overflow-hidden">
                      {isImage ? <img src={url} alt={a.name} className="w-full h-28 object-cover" />
                              : <div className="h-28 flex items-center justify-center text-2xl bg-gray-50">📄</div>}
                      <div className="px-1.5 py-1 text-[10px] text-gray-700 truncate bg-white border-t border-gray-100">{a.name}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resolution */}
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800 mb-1.5">Resolution note (internal)</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      placeholder="Why you resolved/rejected (visible only to admins)"
                      className="w-full px-2 py-1.5 border border-amber-200 rounded-sm text-sm bg-white text-gray-900 resize-none focus:outline-none focus:border-amber-400" />
            {detail.resolved_by && detail.resolved_at && (
              <div className="text-[11px] text-amber-800 mt-1.5">
                ↳ {detail.status === "resolved" ? "Resolved" : "Rejected"} by <b>{detail.resolved_by}</b> · {fmtTime(detail.resolved_at)}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button onClick={() => update("pending")} disabled={saving || detail.status === "pending"} data-testid="ls-sub-mark-pending"
                    className="h-10 rounded-sm border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40 text-xs font-bold inline-flex items-center justify-center gap-1">
              <Clock size={13} /> Pending
            </button>
            <button onClick={() => update("in_progress")} disabled={saving || detail.status === "in_progress"} data-testid="ls-sub-mark-progress"
                    className="h-10 rounded-sm border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40 text-xs font-bold inline-flex items-center justify-center gap-1">
              <Loader2 size={13} /> In Progress
            </button>
            <button onClick={() => update("resolved")} disabled={saving} data-testid="ls-sub-mark-resolved"
                    className="h-10 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold inline-flex items-center justify-center gap-1">
              <CheckCircle2 size={13} /> Resolve
            </button>
            <button onClick={() => update("rejected")} disabled={saving} data-testid="ls-sub-mark-rejected"
                    className="h-10 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 text-xs font-bold inline-flex items-center justify-center gap-1">
              <XCircle size={13} /> Reject
            </button>
          </div>

          {/* Chat thread context */}
          {detail.thread && detail.thread.length > 0 && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Chat thread context</div>
              <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-64 overflow-y-auto bg-gray-50/50">
                {detail.thread.map((m) => (
                  <div key={m.id} className="px-3 py-2 text-[12px]">
                    <span className={`inline-block w-16 font-bold uppercase tracking-wider text-[10px] ${
                      m.sender_type === "agent" ? "text-emerald-700" :
                      m.sender_type === "user" ? "text-purple-700" : "text-gray-500"
                    }`}>{m.sender_type}</span>
                    <span className="text-gray-700 whitespace-pre-wrap break-words">{(m.text || "").slice(0, 200)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
